import { createContext, useContext, useState, useEffect } from 'react';

const VanillaBlocksContext = createContext(null);

export function VanillaBlocksProvider({ children }) {
  const [blocks, setBlocks] = useState([]);
  const [blockColors, setBlockColors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    async function preloadBlocks() {
      try {
        console.log('[VanillaBlocksContext] Starting pre-load...');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vanilla-textures?limit=0`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!mounted) return;
        
        // Extract unique block names from textures
        const blockTextures = data.textures?.filter(t => 
          t.path?.includes('/textures/block/') && 
          !t.path?.includes('_n.png') && // Skip normal maps
          !t.path?.includes('_s.png') && // Skip specular maps
          !t.name?.includes('debug') &&
          !t.name?.includes('particle') &&
          !t.name?.includes('overlay')
        ) || [];
        
        // Create block list with names and paths
        const blockList = blockTextures.map(t => ({
          name: t.name?.replace('.png', '') || '',
          path: t.path,
          displayName: (t.name?.replace('.png', '') || '').replace(/_/g, ' ')
        })).filter(b => b.name);
        
        // Build a simple color lookup (we can expand this later)
        const colors = {};
        blockList.forEach(b => {
          // Basic color estimation from name (fallback)
          colors[b.name] = '#808080'; // Default gray
        });
        
        console.log(`[VanillaBlocksContext] Pre-loaded ${blockList.length} blocks`);
        
        setBlocks(blockList);
        setBlockColors(colors);
        setIsReady(true);
        setError(null);
      } catch (err) {
        console.error('[VanillaBlocksContext] Pre-load error:', err);
        if (mounted) {
          setError(err.message);
          // Don't set isReady to true on error - pages will fall back to their own loading
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }
    
    preloadBlocks();
    
    return () => {
      mounted = false;
    };
  }, []);

  const value = {
    blocks,
    blockColors,
    isLoading,
    isReady,
    error,
    // Helper to get texture URL for a block
    getTextureUrl: (blockName) => {
      return `${process.env.REACT_APP_BACKEND_URL}/api/textures/vanilla/block/${blockName}.png`;
    },
    // Refresh blocks if needed
    refresh: async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vanilla-textures?limit=0`);
        const data = await response.json();
        const blockTextures = data.textures?.filter(t => 
          t.path?.includes('/textures/block/') && 
          !t.path?.includes('_n.png') &&
          !t.path?.includes('_s.png')
        ) || [];
        const blockList = blockTextures.map(t => ({
          name: t.name?.replace('.png', '') || '',
          path: t.path,
          displayName: (t.name?.replace('.png', '') || '').replace(/_/g, ' ')
        })).filter(b => b.name);
        setBlocks(blockList);
        setIsReady(true);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <VanillaBlocksContext.Provider value={value}>
      {children}
    </VanillaBlocksContext.Provider>
  );
}

export function useVanillaBlocks() {
  const context = useContext(VanillaBlocksContext);
  if (!context) {
    // Return a fallback if context is not available (for backwards compatibility)
    return {
      blocks: [],
      blockColors: {},
      isLoading: false,
      isReady: false,
      error: null,
      getTextureUrl: (blockName) => `${process.env.REACT_APP_BACKEND_URL}/api/textures/vanilla/block/${blockName}.png`,
      refresh: () => Promise.resolve()
    };
  }
  return context;
}

export default VanillaBlocksContext;
